import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import type { SagaIterator } from '@redux-saga/types';
import { OM } from './offerConstants';
import * as A from './offerActions';
import type { RootState } from '../store';
import {
  getOfferByIdApi,
  getOffersApi,
  createOfferApi,
  updateOfferApi,
  deleteOfferApi,
  bulkDeleteOffersApi,
  bulkArchiveOffersApi,
  offerStatusToSaveMode,
  getPromoCodesApi,
  createPromoCodeApi,
  updatePromoCodeApi,
  deletePromoCodeApi,
  type ApiOffer,
  type ApiPromoCode,
} from '../../services/offerApi';
import type { BOGOConfig, Offer, PromoCode } from '../../components/offers/data/types';
import type { OffersSummary } from './offerTypes';
import { emptyOffersSummary } from './offerTypes';

// ── Cache ──

type OffersFetchResult = { offers: Offer[]; summary: OffersSummary };

let cachedOffers: Offer[] | null = null;
let cachedOffersSummary: OffersSummary | null = null;
let cachedOffersLocationId: string | null = null;
let lastSuccessfulOffersFetchAt: Record<string, number> = {};
let offersFetchInflight: Record<string, Promise<OffersFetchResult>> = {};

function invalidateOffersCache() {
  cachedOffers = null;
  cachedOffersSummary = null;
  cachedOffersLocationId = null;
  lastSuccessfulOffersFetchAt = {};
  offersFetchInflight = {};
}

function isRecentlyFetched(
  lastFetchAt: Record<string, number>,
  locationId: string,
  skipDuplicateMs?: number,
  force?: boolean,
): boolean {
  if (force) return false;
  return (
    typeof skipDuplicateMs === 'number' &&
    skipDuplicateMs > 0 &&
    lastFetchAt[locationId] != null &&
    Date.now() - lastFetchAt[locationId] < skipDuplicateMs
  );
}

// ── Mappers ──

const OFFER_TYPE_API_TO_INTERNAL: Record<string, string> = {
  PERCENTAGE: 'percentage-off',
  FLAT: 'flat-dollar-off',
  BOGO: 'bogo',
  FREE_ITEM: 'free-item',
  FREE_DELIVERY: 'free-delivery',
  COMBO: 'combo-bundle',
  HAPPY_HOUR: 'happy-hour',
  FIRST_TIME: 'first-time',
  LOYALTY_LINKED: 'loyalty-linked',
};

const STATUS_API_TO_INTERNAL: Record<string, string> = {
  DRAFT: 'draft',
  ACTIVATE: 'active',
  ACTIVE: 'active',
  PAUSED: 'paused',
  EXPIRED: 'expired',
  ARCHIVED: 'archived',
};

const CUSTOMER_PERIOD_API_TO_INTERNAL: Record<string, Offer['eligibility']['redemptionPeriod']> = {
  LIFETIME: 'lifetime',
  DAILY: 'day',
  WEEKLY: 'week',
  MONTHLY: 'month',
};

function itemIdFromBogoRow(row: { itemId?: string; item_id?: string }): string {
  return String(row.itemId ?? row.item_id ?? '').trim();
}

function rowUuidFromBogoRow(row: { id?: string }): string {
  return String(row.id ?? '').trim();
}

/** Map revamp `bogo` block into form/store BOGOConfig for edit flow. */
function mapApiBogoBlockToTypeConfig(raw: unknown): BOGOConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const b = raw as Record<string, unknown>;
  const buyRows = (Array.isArray(b.buyItems)
    ? b.buyItems
    : Array.isArray(b.buy_items)
      ? b.buy_items
      : []) as Array<{ id?: string; itemId?: string; item_id?: string }>;
  const getRows = (Array.isArray(b.getItems)
    ? b.getItems
    : Array.isArray(b.get_items)
      ? b.get_items
      : []) as Array<{ id?: string; itemId?: string; item_id?: string }>;
  const buyItemIds: string[] = [];
  const buyRowIds: string[] = [];
  for (const row of buyRows) {
    const iid = itemIdFromBogoRow(row);
    if (!iid) continue;
    buyItemIds.push(iid);
    buyRowIds.push(rowUuidFromBogoRow(row));
  }
  const getItemIds: string[] = [];
  const getRowIds: string[] = [];
  for (const row of getRows) {
    const iid = itemIdFromBogoRow(row);
    if (!iid) continue;
    getItemIds.push(iid);
    getRowIds.push(rowUuidFromBogoRow(row));
  }
  const buyQuantity = Number(b.buyQty ?? b.buy_qty ?? 1) || 1;
  const getQuantity = Number(b.getQty ?? b.get_qty ?? 1) || 1;
  const rawPct = Number(b.getDiscountPercent ?? b.get_discount_percent ?? 100);
  const getDiscountPercent = Number.isFinite(rawPct) ? Math.min(100, Math.max(0, rawPct)) : 100;
  const equalOrLesserValue = Boolean(b.equalOrLesserValue ?? b.equal_or_lesser_value);
  const excludeRaw = b.excludeItemIds ?? b.exclude_item_ids;
  const excludeItemIds = Array.isArray(excludeRaw)
    ? excludeRaw.map((x) => String(x).trim()).filter(Boolean)
    : [];
  const bogoConfigRaw = b.bogoConfigId ?? b.bogo_config_id;
  const bogoConfigId = bogoConfigRaw != null && String(bogoConfigRaw).trim() ? String(bogoConfigRaw).trim() : undefined;

  return {
    kind: 'bogo',
    buyItemIds,
    buyQuantity,
    getItemIds,
    getQuantity,
    getDiscountPercent,
    equalOrLesserValue,
    excludeItemIds,
    bogoConfigId,
    buyRowIds,
    getRowIds,
  };
}

function mapApiOfferToOffer(api: ApiOffer): Offer {
  // Support both real API fields and legacy snake_case fields
  const id = String(api.offerId ?? api.offer_id ?? '').trim();
  const rawType = api.offerType ?? api.type ?? 'PERCENTAGE';
  const type = (OFFER_TYPE_API_TO_INTERNAL[rawType] ?? rawType) as Offer['type'];
  const rawStatus = api.status ?? 'DRAFT';
  const status = (STATUS_API_TO_INTERNAL[rawStatus] ?? rawStatus.toLowerCase()) as Offer['status'];

  // applicationLevel: API sends 0 = order, 1 = item (or legacy string)
  let applicationLevel: Offer['applicationLevel'] = 'order';
  if (typeof api.applicationLevel === 'number') {
    applicationLevel = api.applicationLevel === 1 ? 'item' : 'order';
  } else if (api.application_level) {
    applicationLevel = api.application_level as Offer['applicationLevel'];
  }

  // autoApply → applicationModes
  const autoApply = Boolean(api.autoApply);
  const applicationModes: Offer['applicationModes'] = api.application_modes
    ? (api.application_modes as Offer['applicationModes'])
    : { inStore: autoApply ? 'auto-apply' : 'manual', digital: autoApply ? 'auto-apply' : 'manual' };

  // orderTypes
  const orderTypes = Array.isArray(api.orderTypeIds)
    ? (api.orderTypeIds as Offer['orderTypes'])
    : Array.isArray(api.order_types)
      ? (api.order_types as Offer['orderTypes'])
      : [];

  // schedule
  const startDate = api.startDate
    ? String(api.startDate).split('T')[0]
    : '';
  const endDate = api.endDate
    ? String(api.endDate).split('T')[0]
    : null;
  const noEndDate = api.noEndDate ?? !endDate;
  const apiSchedule = api.schedule && typeof api.schedule === 'object'
    ? (api.schedule as {
        startDate?: string;
        endDate?: string | null;
        noEndDate?: boolean;
        recurring?: Offer['schedule']['recurring'];
        enabled?: boolean;
        days?: number[];
        allDay?: boolean;
        startTime?: string;
        endTime?: string;
      })
    : null;
  const hasFlatRecurringFields =
    Boolean(apiSchedule?.enabled) ||
    (Array.isArray(apiSchedule?.days) && apiSchedule!.days.length > 0) ||
    Boolean(apiSchedule?.startTime) ||
    Boolean(apiSchedule?.endTime);
  const recurringFromApi =
    apiSchedule?.recurring ??
    (hasFlatRecurringFields
      ? {
          days: Array.isArray(apiSchedule?.days) ? apiSchedule.days : [],
          allDay: apiSchedule?.allDay ?? true,
          startTime: apiSchedule?.startTime ?? '00:00',
          endTime: apiSchedule?.endTime ?? '23:45',
        }
      : null);
  const schedule: Offer['schedule'] = {
    startDate: apiSchedule?.startDate
      ? String(apiSchedule.startDate).split('T')[0]
      : startDate,
    endDate: apiSchedule?.endDate
      ? String(apiSchedule.endDate).split('T')[0]
      : endDate,
    noEndDate: apiSchedule?.noEndDate ?? noEndDate,
    recurring: recurringFromApi,
  };

  // eligibility — build from flat API fields
  const itemElig = api.itemEligibility;
  const requiredItemIds = itemElig?.mode === 'INCLUDE' ? (itemElig.itemIds ?? []) : [];
  const excludedItemIds = itemElig?.mode === 'EXCLUDE' ? (itemElig.itemIds ?? []) : [];
  const eligibility: Offer['eligibility'] = api.eligibility && typeof api.eligibility === 'object'
    ? (api.eligibility as Offer['eligibility'])
    : {
        firstTimeOnly: Boolean(api.isFirstTimeOnly),
        minCheckSubtotal: (() => {
          const v = api.minSubtotal;
          if (v == null) return null;
          const n = Number(v);
          return Number.isFinite(n) && n > 0 ? n : null;
        })(),
        maxCheckSubtotal: null,
        requiredItemIds,
        requiredCategoryNames: [],
        excludedItemIds,
        excludedCategoryNames: [],
        maxRedemptionsTotal: (() => {
          const raw = api.maxCap;
          if (raw == null) return null;
          const n = Number(raw);
          return Number.isFinite(n) && n > 0 ? n : null;
        })(),
        // API often sends 0 meaning "no per-customer cap" — treat as null so Save & Activate validation passes.
        maxRedemptionsPerCustomer: (() => {
          const raw = api.perCustomerLimit ?? api.per_customer_limit;
          if (raw == null) return null;
          const n = Number(raw);
          if (!Number.isFinite(n) || n < 1) return null;
          return n;
        })(),
        redemptionPeriod:
          CUSTOMER_PERIOD_API_TO_INTERNAL[
            String(api.customerPeriod ?? api.customer_period ?? 'LIFETIME').toUpperCase()
          ] ?? 'lifetime',
      };

  const promoCodes = Array.isArray(api.promoCodes)
    ? api.promoCodes.map(mapApiPromoCodeToPromoCode)
    : Array.isArray(api.promo_codes)
      ? api.promo_codes.map(mapApiPromoCodeToPromoCode)
      : [];

  const typeConfigFromApi = (api.type_config ?? null) as Offer['typeConfig'];
  const bogoFromNested =
    type === 'bogo' && api.bogo != null ? mapApiBogoBlockToTypeConfig(api.bogo) : null;
  const freeItemFromApi: Offer['typeConfig'] =
    type === 'free-item'
      ? {
          kind: 'free-item',
          minSpend: Number(api.minSubtotal ?? 0) || 0,
          freeItemIds: itemElig?.itemIds ?? [],
          maxFreeItems: Number(api.noOfFreeItems ?? 1) || 1,
        }
      : null;
  const typeConfig: Offer['typeConfig'] = bogoFromNested ?? freeItemFromApi ?? typeConfigFromApi;

  return {
    id,
    name: String(api.name ?? '').trim(),
    description: typeof api.description === 'string' ? api.description.trim() : String(api.description ?? '').trim(),
    internalNotes: typeof api.internalNotes === 'string' ? api.internalNotes.trim() : String(api.internal_notes ?? '').trim(),
    type,
    discountValue: Number(api.discountValue ?? api.discount_value ?? 0),
    maxDiscountCap: (() => {
      const raw = api.maxDiscountCap ?? api.max_discount_cap;
      if (raw == null) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    })(),
    applicationLevel,
    orderTypes,
    locationScope: (api.location_scope ?? 'all') as Offer['locationScope'],
    locationIds: Array.isArray(api.location_ids) ? api.location_ids : [],
    locationGroupIds: Array.isArray(api.location_group_ids) ? api.location_group_ids : [],
    status,
    isExclusive: Boolean(api.isExclusive ?? api.is_exclusive),
    origin: (api.origin ?? 'local') as Offer['origin'],
    typeConfig,
    schedule,
    eligibility,
    promoCodes,
    locationOverrides: Array.isArray(api.location_overrides)
      ? (api.location_overrides as Offer['locationOverrides'])
      : [],
    applicationModes,
    permittedRoles: Array.isArray(api.permitted_roles) ? api.permitted_roles : [],
    managerOverrideRequired: Boolean(api.requiresManagerApproval ?? api.manager_override_required),
    redemptionCount: Number(api.offerAppliedCount ?? api.redemption_count ?? 0),
    version: Number(api.version ?? 1),
    createdAt: String(api.created_at ?? api.createdAt ?? new Date().toISOString()),
    updatedAt: String(api.updated_at ?? api.updatedAt ?? new Date().toISOString()),
  };
}

function mapApiPromoCodeToPromoCode(api: ApiPromoCode): PromoCode {
  const id = String(api.promoCodeId ?? api.promo_code_id ?? '').trim();
  // Real API uses "name" as the code string; legacy uses "code"
  const code = String(api.name ?? api.code ?? '').trim();
  const maxRedemptions = api.max_usage ?? api.max_redemptions ?? null;
  const inferredSingleUse = maxRedemptions != null && Number(maxRedemptions) === 1;
  const effectiveFrom = api.effectiveFrom ?? api.effective_from ?? api.valid_from ?? null;
  const effectiveUntil = api.effectiveUntil ?? api.effective_until ?? api.valid_to ?? null;
  return {
    id,
    code,
    description: String(api.description ?? '').trim(),
    offerId: String(api.offerId ?? api.offer_id ?? '').trim(),
    effectiveFrom,
    effectiveUntil,
    singleUse: inferredSingleUse,
    maxRedemptions: maxRedemptions !== null ? Number(maxRedemptions) : null,
    usageCount: Number(api.usageCount ?? api.usage_count ?? 0),
    status: (api.status ?? 'active') as PromoCode['status'],
    createdAt: String(api.createdAt ?? api.created_at ?? new Date().toISOString()),
  };
}

// ── Workers ──

function* fetchOffersWorker(action: ReturnType<typeof A.fetchOffersRequest>): SagaIterator {
  const { locationId, token, options, deferred } = action.payload;
  const force = Boolean(options?.force);
  const skipDuplicateMs = options?.skipDuplicateMs;
  try {
    if (!locationId) {
      yield put(A.offersClearCache());
      deferred?.resolve();
      return;
    }
    if (isRecentlyFetched(lastSuccessfulOffersFetchAt, locationId, skipDuplicateMs, force)) {
      deferred?.resolve();
      return;
    }
    const state: RootState = yield select();
    if (!force && state.offerManagement.offers.loadedLocationId === locationId) {
      yield put(A.fetchOffersSuccess({
        offers: state.offerManagement.offers.offers,
        locationId,
        summary: state.offerManagement.offers.summary,
      }));
      deferred?.resolve();
      return;
    }
    if (!force && cachedOffers && cachedOffersLocationId === locationId) {
      yield put(A.fetchOffersSuccess({
        offers: cachedOffers,
        locationId,
        summary: cachedOffersSummary ?? emptyOffersSummary,
      }));
      deferred?.resolve();
      return;
    }
    if (force) {
      invalidateOffersCache();
    }
    const inflightKey = `${locationId}|${token ?? ''}`;
    if (!offersFetchInflight[inflightKey]) {
      offersFetchInflight[inflightKey] = (async () => {
        const resp = await getOffersApi({ locationId }, token);
        const apiOffers = Array.isArray(resp.data?.offers) ? resp.data.offers : [];
        const summary: OffersSummary = {
          totalActiveOffersCount: Number(resp.data?.totalActiveOffersCount ?? 0),
          totalRedemptions: Number(resp.data?.totalRedemptions ?? 0),
          totalDiscountAmount: Number(resp.data?.totalDiscountAmount ?? 0),
          totalOrderAmount: Number(resp.data?.totalOrderAmount ?? 0),
        };
        return { offers: apiOffers.map(mapApiOfferToOffer), summary };
      })();
      void offersFetchInflight[inflightKey].finally(() => {
        delete offersFetchInflight[inflightKey];
      });
    }
    const result: OffersFetchResult = yield call(() => offersFetchInflight[inflightKey]);
    const { offers, summary } = result;
    cachedOffers = offers;
    cachedOffersSummary = summary;
    cachedOffersLocationId = locationId;
    lastSuccessfulOffersFetchAt[locationId] = Date.now();
    yield put(A.fetchOffersSuccess({ offers, locationId, summary }));
    deferred?.resolve();
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Failed to fetch offers';
    yield put(A.fetchOffersFailure({ error: msg }));
    deferred?.reject(e instanceof Error ? e : new Error(msg));
  }
}

function* fetchOfferByIdWorker(action: ReturnType<typeof A.fetchOfferByIdRequest>): SagaIterator {
  const { offerId, locationId, token, deferred } = action.payload;
  try {
    const apiOffer: ApiOffer = yield call(getOfferByIdApi, offerId, locationId, token);
    const offer = mapApiOfferToOffer(apiOffer);
    yield put(A.fetchOfferByIdSuccess({ offer }));
    deferred?.resolve(offer);
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Failed to fetch offer';
    yield put(A.fetchOfferByIdFailure({ error: msg }));
    deferred?.reject(e instanceof Error ? e : new Error(msg));
  }
}

function* createOfferWorker(action: ReturnType<typeof A.createOfferRequest>): SagaIterator {
  const { data, token, deferred } = action.payload;
  try {
    const apiOffer: ApiOffer = yield call(createOfferApi, data, token);
    console.log({apiOffer});
    
    const offer = mapApiOfferToOffer(apiOffer);
    invalidateOffersCache();
    yield put(A.createOfferDone({ offer }));
    deferred?.resolve(offer);
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Failed to create offer';
    yield put(A.createOfferDone({ error: msg }));
    deferred?.reject(e instanceof Error ? e : new Error(msg));
  }
}

function* updateOfferWorker(action: ReturnType<typeof A.updateOfferRequest>): SagaIterator {
  const { data, token, deferred } = action.payload;
  try {
    const { offerId, data: updates } = data;
    const apiOffer: ApiOffer = yield call(updateOfferApi, offerId, updates, token);
    const updatedOffer = mapApiOfferToOffer(apiOffer);
    invalidateOffersCache();
    yield put(A.offersUpdate({ id: offerId, updates: updatedOffer }));
    yield put(A.updateOfferDone({}));
    deferred?.resolve();
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Failed to update offer';
    yield put(A.updateOfferDone({ error: msg }));
    deferred?.reject(e instanceof Error ? e : new Error(msg));
  }
}

function* deleteOfferWorker(action: ReturnType<typeof A.deleteOfferRequest>): SagaIterator {
  const { offerId, locationId, token, deferred } = action.payload;
  try {
    yield call(deleteOfferApi, offerId, locationId, token);
    invalidateOffersCache();
    yield put(A.deleteOfferDone({ offerId }));
    deferred?.resolve();
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Failed to delete offer';
    yield put(A.deleteOfferDone({ error: msg }));
    deferred?.reject(e instanceof Error ? e : new Error(msg));
  }
}

function* toggleOfferStatusWorker(action: ReturnType<typeof A.toggleOfferStatusRequest>): SagaIterator {
  const { data, token, deferred } = action.payload;
  try {
    const saveMode = offerStatusToSaveMode(data.status);
    const apiOffer: ApiOffer = yield call(updateOfferApi, data.offerId, { locationId: data.locationId, saveMode }, token);
    const updatedOffer = mapApiOfferToOffer(apiOffer);
    invalidateOffersCache();
    yield put(A.offersUpdate({ id: data.offerId, updates: updatedOffer }));
    yield put(A.toggleOfferStatusDone({}));
    deferred?.resolve();
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Failed to toggle offer status';
    yield put(A.toggleOfferStatusDone({ error: msg }));
    deferred?.reject(e instanceof Error ? e : new Error(msg));
  }
}

function* bulkDeleteOffersWorker(action: ReturnType<typeof A.bulkDeleteOffersRequest>): SagaIterator {
  const { data, token, deferred } = action.payload;
  try {
    yield call(bulkDeleteOffersApi, data.offerIds, data.locationId, token);
    invalidateOffersCache();
    yield put(A.bulkDeleteOffersDone({ offerIds: data.offerIds }));
    deferred?.resolve();
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Failed to delete offers';
    yield put(A.bulkDeleteOffersDone({ error: msg }));
    deferred?.reject(e instanceof Error ? e : new Error(msg));
  }
}

function* bulkArchiveOffersWorker(action: ReturnType<typeof A.bulkArchiveOffersRequest>): SagaIterator {
  const { data, token, deferred } = action.payload;
  try {
    yield call(bulkArchiveOffersApi, data.offerIds, token);
    invalidateOffersCache();
    yield put(A.bulkArchiveOffersDone({ offerIds: data.offerIds }));
    deferred?.resolve();
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Failed to archive offers';
    yield put(A.bulkArchiveOffersDone({ error: msg }));
    deferred?.reject(e instanceof Error ? e : new Error(msg));
  }
}

function* fetchPromoCodesWorker(action: ReturnType<typeof A.fetchPromoCodesRequest>): SagaIterator {
  const { offerId, locationId, token, deferred } = action.payload;
  try {
    const apiCodes: ApiPromoCode[] = yield call(getPromoCodesApi, offerId, locationId, token);
    const promoCodes = apiCodes.map(mapApiPromoCodeToPromoCode);
    yield put(A.fetchPromoCodesSuccess({ promoCodes }));
    deferred?.resolve();
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Failed to fetch promo codes';
    yield put(A.fetchPromoCodesFailure({ error: msg }));
    deferred?.reject(e instanceof Error ? e : new Error(msg));
  }
}

function* createPromoCodeWorker(action: ReturnType<typeof A.createPromoCodeRequest>): SagaIterator {
  const { data, token, deferred } = action.payload;
  try {
    const { offerId, data: codeData } = data;
    const apiCode: ApiPromoCode = yield call(createPromoCodeApi, offerId, codeData, token);
    const promoCode = mapApiPromoCodeToPromoCode(apiCode);
    yield put(A.createPromoCodeDone({ promoCode }));
    deferred?.resolve(promoCode);
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Failed to create promo code';
    yield put(A.createPromoCodeDone({ error: msg }));
    deferred?.reject(e instanceof Error ? e : new Error(msg));
  }
}

function* updatePromoCodeWorker(action: ReturnType<typeof A.updatePromoCodeRequest>): SagaIterator {
  const { data, token, deferred } = action.payload;
  try {
    const { offerId, promoCodeId, data: updates } = data;
    const apiCode: ApiPromoCode = yield call(updatePromoCodeApi, offerId, promoCodeId, updates, token);
    const updatedCode = mapApiPromoCodeToPromoCode(apiCode);
    yield put(A.promoCodesUpdate({ id: promoCodeId, updates: updatedCode }));
    yield put(A.updatePromoCodeDone({}));
    deferred?.resolve();
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Failed to update promo code';
    yield put(A.updatePromoCodeDone({ error: msg }));
    deferred?.reject(e instanceof Error ? e : new Error(msg));
  }
}

function* deletePromoCodeWorker(action: ReturnType<typeof A.deletePromoCodeRequest>): SagaIterator {
  const { offerId, promoCodeId, locationId, token, deferred } = action.payload;
  try {
    yield call(deletePromoCodeApi, offerId, promoCodeId, locationId, token);
    yield put(A.deletePromoCodeDone({ promoCodeId }));
    deferred?.resolve();
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Failed to delete promo code';
    yield put(A.deletePromoCodeDone({ error: msg }));
    deferred?.reject(e instanceof Error ? e : new Error(msg));
  }
}

// ── Workers ──

function* clearCacheWorker(): SagaIterator {
  invalidateOffersCache();
}

// ── Root saga ──

export function* offerManagementSaga(): SagaIterator {
  yield all([
    takeLatest(OM.OFFERS_CLEAR_CACHE, clearCacheWorker),
    takeLatest(OM.FETCH_OFFER_BY_ID_REQUEST, fetchOfferByIdWorker),
    takeLatest(OM.FETCH_OFFERS_REQUEST, fetchOffersWorker),
    takeLatest(OM.CREATE_OFFER_REQUEST, createOfferWorker),
    takeLatest(OM.UPDATE_OFFER_REQUEST, updateOfferWorker),
    takeLatest(OM.DELETE_OFFER_REQUEST, deleteOfferWorker),
    takeLatest(OM.BULK_DELETE_OFFERS_REQUEST, bulkDeleteOffersWorker),
    takeLatest(OM.TOGGLE_OFFER_STATUS_REQUEST, toggleOfferStatusWorker),
    takeLatest(OM.BULK_ARCHIVE_OFFERS_REQUEST, bulkArchiveOffersWorker),
    takeLatest(OM.FETCH_PROMO_CODES_REQUEST, fetchPromoCodesWorker),
    takeLatest(OM.CREATE_PROMO_CODE_REQUEST, createPromoCodeWorker),
    takeLatest(OM.UPDATE_PROMO_CODE_REQUEST, updatePromoCodeWorker),
    takeLatest(OM.DELETE_PROMO_CODE_REQUEST, deletePromoCodeWorker),
  ]);
}
